import React from 'react';
import { useLocation } from 'react-router-dom';
import { getServiceLanding } from '@/lib/serviceLandingPages';
import { ServiceNextSteps } from '@/components/trinity/ServiceConversion';
import { motion } from 'framer-motion';
import PatientQuiz from '@/components/trinity/PatientQuiz';
import { MapPin, Phone, Clock } from 'lucide-react';

const contactInfo = [
  { icon: MapPin, title: 'Visit Us', lines: ['11105 Trinity Blvd', 'Trinity, FL 34655'] },
  { icon: Phone, title: 'Call Us', lines: ['(727) 228-6846'] },
  { icon: Clock, title: 'Office Hours', lines: ['Mon-Thu: 9:00am - 5:00pm', 'Fri-Sun: Closed'] },
];

export default function ContactSection() {
  const service = getServiceLanding(useLocation().pathname);
  if (service) return null;
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <h2 className="text-4xl lg:text-5xl font-light text-slate-800 mb-4">
            Get In <span className="font-serif italic text-amber-700">Touch</span>
          </h2>
          <p className="text-lg text-slate-600">
            Book the <a className="underline underline-offset-4" href="/book-appointment#signature-consult">in-office $300 Signature Consult</a> online for a clinical exam and a written treatment plan from Dr. Gjelaj. The fee is credited in full toward treatment begun within 30 days.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <PatientQuiz source="contact-section" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-5"
          >
            {contactInfo.map(info => (
              <div key={info.title} className="bg-white rounded-2xl p-6 shadow-lg shadow-slate-200/50">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-800 mb-1">{info.title}</h3>
                    {info.lines.map((line, i) => (
                      <p key={i} className="text-slate-600 text-sm">{line}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <p className="text-amber-800 font-medium mb-1">✓ Your Signature Consultation</p>
              <p className="text-amber-700 text-sm">An in-office <strong>Signature Consultation ($300)</strong> with Dr. Gjelaj, which includes a full exam, 3D CBCT imaging, smile design, and a written treatment plan — credited in full toward treatment begun within 30 days.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}